import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RequestStatusBadge } from "@/components/Badges";
import { api, errMsg, fmtDate } from "@/lib/api";

export default function RequestPeptide() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ peptide_name: "", details: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/my/requests").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/my/requests", form);
      toast.success("Request sent to Korevex Bio");
      setForm({ peptide_name: "", details: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:py-14">
      <div className="rise">
        <p className="eyebrow mb-3">Can't find it?</p>
        <h1 className="text-4xl font-bold">Request a peptide</h1>
        <p className="mt-3 text-muted-foreground">Tell us what you're looking for and we'll let you know if we can carry it.</p>
        <form onSubmit={submit} className="mt-8 space-y-4 rounded-lg border border-border bg-card p-6">
          <div className="space-y-2">
            <Label htmlFor="pn">Peptide name</Label>
            <Input id="pn" required value={form.peptide_name} onChange={(e) => setForm({ ...form, peptide_name: e.target.value })} data-testid="request-peptide-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pd">Details (size, quantity, anything else)</Label>
            <Textarea id="pd" rows={4} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="request-peptide-details-input" />
          </div>
          <Button type="submit" disabled={busy} data-testid="request-peptide-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send request
          </Button>
        </form>
      </div>
      <div>
        <h2 className="mb-4 text-lg font-medium">Your requests</h2>
        {list.length === 0 && <p className="text-sm text-muted-foreground" data-testid="my-requests-empty">No requests yet.</p>}
        <div className="space-y-3">
          {list.map((r) => (
            <div key={r.id} className="rounded-lg border border-border bg-card p-5" data-testid={`my-request-${r.id}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium">{r.peptide_name}</p>
                <RequestStatusBadge status={r.status} />
              </div>
              {r.details && <p className="mt-2 text-sm text-muted-foreground">{r.details}</p>}
              {r.admin_reply && <p className="mt-3 border-l-2 border-emerald-500 pl-3 text-sm">{r.admin_reply}</p>}
              <p className="mt-2 font-mono-data text-xs text-muted-foreground">{fmtDate(r.created_at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
